import React, { useCallback, useMemo, useState } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import useErrorLogStore from '../../store/error-logs-store';
import usePaginatedListPage from '../../shared/hooks/usePaginatedListPage';
import DataTable from '../../shared/components/table/DataTable';
import RowActions from '../../shared/components/table/RowActions';
import SearchInput from '../../shared/components/table/SearchInput';
import FilterSelect from '../../shared/components/table/FilterSelect';
import ClearFiltersButton from '../../shared/components/table/ClearFiltersButton';
import ListFooter from '../../shared/components/table/ListFooter';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import { DateInput } from '../../shared/components/DateInput';
import { formatDate } from '../../shared/lib/formatters';
import { renderLargeCell } from '../../shared/lib/renderLargeCell';

const columnHelper = createColumnHelper();
const stages = ['Frontend', 'Server Side', 'Transcription', 'Generation'];
const stageOptions = stages.map((s) => ({ value: s.toLowerCase().replace(/\s+/g, ''), label: s }));

const ErrorLogsPage = () => {
  const { errorLogs, totalErrorLogs, totalPages, fetchErrorLogs, deleteErrorLog } = useErrorLogStore();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [stage, setStage] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedErrorLog, setSelectedErrorLog] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const startDate = useMemo(() => {
    if (!dateFrom) return '';
    const date = new Date(dateFrom);
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  }, [dateFrom]);

  const endDate = useMemo(() => {
    if (!dateTo) return '';
    const date = new Date(dateTo);
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  }, [dateTo]);

  const fetchData = useCallback(
    async (page, search, stageArg, startDateArg, endDateArg) => {
      await fetchErrorLogs(page, search, stageArg, startDateArg, endDateArg);
    },
    [fetchErrorLogs]
  );

  const {
    page: currentPage,
    setPage: setCurrentPage,
    search: globalFilter,
    setSearch: setGlobalFilter,
    isLoading,
  } = usePaginatedListPage({
    fetchItems: fetchData,
    deps: [stage, startDate, endDate],
  });

  const handleOpenDeleteModal = (errorLog) => {
    setSelectedErrorLog(errorLog);
    setIsDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedErrorLog(null);
  };

  const handleDeleteErrorLog = async (id) => {
    try {
      await deleteErrorLog(id);
      toast.success('Error Log deleted successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Failed to delete Error Log:', error);
      toast.error('Failed to delete Error Log. Please try again.');
    }
  };

  const clearFilters = () => {
    setGlobalFilter('');
    setStage('');
    setDateFrom('');
    setDateTo('');
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('row_number', { header: 'Report ID' }),
      columnHelper.accessor('stage', { header: 'Stage' }),
      columnHelper.accessor('error_message', {
        header: 'Error Message',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'error_message'),
      }),
      columnHelper.accessor('stack_trace', {
        header: 'Stack Trace',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'stack_trace'),
      }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => <RowActions onDelete={() => handleOpenDeleteModal(row.original)} />,
      }),
    ],
    [expandedRows]
  );

  const table = useReactTable({
    data: errorLogs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const noLogsFound = table.getRowModel().rows.length === 0;

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <h1 className="text-2xl font-semibold mb-4 py-1">Error Logs List</h1>

      <div className="flex flex-wrap gap-4 mb-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
        <FilterSelect
          name="stage"
          value={stage}
          onChange={setStage}
          options={stageOptions}
          placeholder="Select Stage"
        />
        <DateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="From Date" />
        <DateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="To Date" />
        <ClearFiltersButton show={!!(globalFilter || stage || dateFrom || dateTo)} onClick={clearFilters} />
      </div>

      <DataTable table={table} columnsCount={columns.length} isLoading={isLoading} emptyMessage="No error logs found" />

      <ListFooter
        total={totalErrorLogs}
        totalPages={totalPages}
        page={currentPage}
        setPage={setCurrentPage}
        isLoading={isLoading}
        isEmpty={noLogsFound}
        itemLabel="logs"
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        data={{ ...selectedErrorLog, dataName: 'error log' }}
        onDelete={handleDeleteErrorLog}
      />
    </div>
  );
};

export default ErrorLogsPage;
